import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TechServiceSelectStep from './TechServiceSelectStep';
import DateAndTimeSelectStep from './DateAndTimeSelectStep';
import TechnicianSelectStep from './TechnicianSelectStep';
import './AppointmentWizardModal.css';

const AppointmentWizardModal = ({
  isOpen,
  onClose,
  onSubmit,
  serviceRecordId,
  autoSelectedTechService,
  submitting = false,
  error = ''
}) => {
  const { user } = useAuth();
  const isTechService = user?.rol === 'tech-service';
  
  // Tech-service kullanıcıları için teknik servis seçimi adımını atla
  const initialStep = isTechService && autoSelectedTechService ? 1 : 1;
  const totalSteps = isTechService ? 2 : 3; // Tech-service: Tarih/Saat, Teknisyen (2 adım) | Admin/Mod: Teknik Servis, Tarih/Saat, Teknisyen (3 adım)
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [selectedTechService, setSelectedTechService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSlotKodu, setSelectedSlotKodu] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [formError, setFormError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const startStep = isTechService && autoSelectedTechService ? 1 : 1;
      setCurrentStep(startStep);
      setSelectedTechService(autoSelectedTechService || null);
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedSlotKodu(null);
      setSelectedTechnician(null);
      setFormError('');
    }
  }, [isOpen, autoSelectedTechService, isTechService]);

  // Update error from parent
  useEffect(() => {
    if (error) {
      setFormError(error);
    }
  }, [error]);

  const handleNext = () => {
    // Validation
    if (isTechService) {
      // Tech-service kullanıcıları için: Adım 1 = Tarih/Saat, Adım 2 = Teknisyen (son adım)
      if (currentStep === 1) {
        if (!selectedDate) {
          setFormError('Lütfen bir tarih seçiniz');
          return;
        }
        if (!selectedTime) {
          setFormError('Lütfen bir saat seçiniz');
          return;
        }
        setFormError('');
        setCurrentStep(2);
      }
      // currentStep === 2 için handleNext çağrılmamalı, direkt submit olmalı
    } else {
      // Admin/Mod kullanıcıları için: Adım 1 = Teknik Servis, Adım 2 = Tarih/Saat, Adım 3 = Teknisyen
      if (currentStep === 1) {
        if (!selectedTechService) {
          setFormError('Lütfen bir teknik servis seçiniz');
          return;
        }
        setFormError('');
        setCurrentStep(2);
      } else if (currentStep === 2) {
        if (!selectedDate) {
          setFormError('Lütfen bir tarih seçiniz');
          return;
        }
        if (!selectedTime) {
          setFormError('Lütfen bir saat seçiniz');
          return;
        }
        setFormError('');
        setCurrentStep(3);
      } else if (currentStep === 3) {
        if (!selectedTechnician) {
          setFormError('Lütfen bir teknisyen seçiniz');
          return;
        }
        setFormError('');
        // Bu adım son adım, submit'e geç
      }
    }
  };

  const handleBack = () => {
    setFormError('');
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isTechService) {
      // Tech-service kullanıcıları için validasyon
      if (!selectedDate) {
        setFormError('Lütfen bir tarih seçiniz');
        return;
      }
      if (!selectedTime) {
        setFormError('Lütfen bir saat seçiniz');
        return;
      }
      if (!selectedTechnician) {
        setFormError('Lütfen bir teknisyen seçiniz');
        return;
      }
    } else {
      // Admin/Mod kullanıcıları için validasyon
      if (!selectedTechService) {
        setFormError('Lütfen bir teknik servis seçiniz');
        return;
      }
      if (!selectedDate) {
        setFormError('Lütfen bir tarih seçiniz');
        return;
      }
      if (!selectedTime) {
        setFormError('Lütfen bir saat seçiniz');
        return;
      }
      if (!selectedTechnician) {
        setFormError('Lütfen bir teknisyen seçiniz');
        return;
      }
    }

    // Tarihi YYYY-MM-DD formatına çevir (timezone sorununu önlemek için)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const techServiceId = isTechService 
      ? (autoSelectedTechService?.id || autoSelectedTechService?._id || user?.techService || user?.techServiceId)
      : (selectedTechService?.id || selectedTechService?._id);

    onSubmit({
      teknikServisId: techServiceId,
      randevuTarihi: formatDate(selectedDate),
      randevuSaati: selectedTime,
      randevuSlotKodu: selectedSlotKodu,
      teknisyenId: selectedTechnician?.id || selectedTechnician?._id,
    });
  };

  const canProceed = () => {
    if (isTechService) {
      // Tech-service kullanıcıları için
      if (currentStep === 1) {
        return !!selectedDate && !!selectedTime;
      }
      if (currentStep === 2) {
        return !!selectedTechnician;
      }
    } else {
      // Admin/Mod kullanıcıları için
      if (currentStep === 1) {
        return !!selectedTechService;
      }
      if (currentStep === 2) {
        return !!selectedDate && !!selectedTime;
      }
      if (currentStep === 3) {
        return !!selectedTechnician;
      }
    }
    return false;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay appointment-wizard-overlay" onClick={onClose}>
      <div className="modal appointment-wizard-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            Yeni Randevu Oluştur
            <span className="step-indicator">Adım {currentStep} / {totalSteps}</span>
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Kapat"
            disabled={submitting}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form className="modal-body wizard-body" onSubmit={handleSubmit}>
          {isTechService ? (
            // Tech-service kullanıcıları için: Adım 1 = Tarih/Saat, Adım 2 = Teknisyen
            <>
              {currentStep === 1 && (
                <DateAndTimeSelectStep
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  selectedSlotKodu={selectedSlotKodu}
                  setSelectedSlotKodu={setSelectedSlotKodu}
                  teknikServisId={autoSelectedTechService?.id || autoSelectedTechService?._id || user?.techService || user?.techServiceId}
                />
              )}

              {currentStep === 2 && (
                <TechnicianSelectStep
                  selectedTechnician={selectedTechnician}
                  setSelectedTechnician={setSelectedTechnician}
                  techServiceId={autoSelectedTechService?.id || autoSelectedTechService?._id || user?.techService || user?.techServiceId}
                />
              )}
            </>
          ) : (
            // Admin/Mod kullanıcıları için: Adım 1 = Teknik Servis, Adım 2 = Tarih/Saat, Adım 3 = Teknisyen
            <>
              {currentStep === 1 && (
                <TechServiceSelectStep
                  selectedTechService={selectedTechService}
                  setSelectedTechService={setSelectedTechService}
                  autoSelectedTechService={autoSelectedTechService}
                  serviceRecordId={serviceRecordId}
                />
              )}

              {currentStep === 2 && (
                <DateAndTimeSelectStep
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  selectedSlotKodu={selectedSlotKodu}
                  setSelectedSlotKodu={setSelectedSlotKodu}
                  teknikServisId={selectedTechService?.id || selectedTechService?._id}
                />
              )}

              {currentStep === 3 && (
                <TechnicianSelectStep
                  selectedTechnician={selectedTechnician}
                  setSelectedTechnician={setSelectedTechnician}
                  techServiceId={selectedTechService?.id || selectedTechService?._id}
                />
              )}
            </>
          )}

          {formError && (
            <div className="form-error" style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              color: '#ef4444',
              padding: '12px 16px',
              borderRadius: '8px',
              marginTop: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-exclamation-circle"></i>
              <span>{formError}</span>
            </div>
          )}
        </form>

        <div className="modal-footer wizard-footer">
          {currentStep > 1 && (
            <button
              type="button"
              className="secondary-btn"
              onClick={handleBack}
              disabled={submitting}
            >
              <i className="fas fa-arrow-left"></i>
              <span>Geri</span>
            </button>
          )}

          <div style={{ flex: 1 }}></div>

          <button
            type="button"
            className="secondary-btn"
            onClick={onClose}
            disabled={submitting}
          >
            İptal
          </button>

          {currentStep < totalSteps ? (
            <button
              type="button"
              className="primary-btn"
              onClick={handleNext}
              disabled={!canProceed() || submitting}
            >
              <span>İleri</span>
              <i className="fas fa-arrow-right"></i>
            </button>
          ) : (
            <button
              type="button"
              className="primary-btn"
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Oluşturuluyor...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  <span>Randevu Oluştur</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentWizardModal;
